import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useLambda } from '../../../../models/hooks/useLambda';
import { useEventLogging } from '../../../../models/Logging';
import {
	useAutomationTemplateStepSwitchAddCaseMutation,
	useAutomationTemplateStepSwitchUpdateCaseMutation,
} from '../../../../queries/Automation';
import { navigation } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { RoundAddIcon } from '../../svgs/icons/RoundAddIcon';
import { EditAutomationSegmentAdditionalFilters, EditAutomationSegmentPrimaryFilter } from './presentation';
import { styleSheet } from './styles';

interface IProps {
	templateId: string;
	stepId: string;
	impersonationContext?: Api.IImpersonationContext;
	className?: string;
	styles?: StyleDeclarationValue[];
	caseStatement: Api.IAutomationStepCaseStatement;
	switchStep: Api.ISwitchAutomationStep;
	onStepSaved: () => void;
}

export const EditAutomationTemplateSegmentHeader: React.FC<IProps> = props => {
	const {
		className,
		styles = [],
		caseStatement,
		switchStep,
		templateId,
		stepId,
		impersonationContext,
		onStepSaved,
	} = props;
	const { logEvent, logApiError } = useEventLogging('EditAutomationTemplateSegmentHeader');
	const errorMessages = useErrorMessages();

	// React Query mutations
	const addCaseMutation = useAutomationTemplateStepSwitchAddCaseMutation({
		onSuccess: () => {
			onStepSaved();
		},
		onError: error => {
			logApiError('SaveCaseStatement-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const updateCaseMutation = useAutomationTemplateStepSwitchUpdateCaseMutation({
		onSuccess: () => {
			onStepSaved();
		},
		onError: error => {
			logApiError('SaveCaseStatement-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	// #region config filters
	const [primaryCriteria, setPrimaryCriteria] = React.useState<Api.IContactFilterCriteria>(() => {
		const sorted = Api.VmUtils.sortContactFilterCriteria(caseStatement?.filter?.criteria);
		const criteriaCollection = [...sorted.filters, ...sorted.searches];
		const first = criteriaCollection?.[0];
		return first || null;
	});

	const [additionalTagFilters, setAdditionalTagFilters] = React.useState<Api.IContactFilterCriteria[]>(() => {
		const sorted = Api.VmUtils.sortContactFilterCriteria(caseStatement?.filter?.criteria);
		return sorted.searches.filter(x => Api.VmUtils.isTagSearchContactFilterCriteria(x) && x !== primaryCriteria);
	});

	const [showAdditionalFilters, , setShowAdditionalFiltersLambda] = useLambda(
		() => additionalTagFilters?.length > 0,
		[]
	);
	// #endregion

	// #region handle filter changes
	const saveCaseStatement = React.useCallback(
		async (primary?: Api.IContactFilterCriteria, additional = additionalTagFilters) => {
			const next: Api.IContactFilterCriteria = primary
				? {
						criteria: [primary, ...(additional || [])],
						op: Api.FilterOperator.And,
					}
				: null;
			const nextCase: Api.IAutomationStepCaseStatementBase = {
				...(caseStatement || {}),
				filter: next,
				isDefault: !next,
			};

			logEvent('SaveCaseStatement');
			await (caseStatement.id
				? updateCaseMutation.mutateAsync({
						templateId,
						stepId,
						caseId: caseStatement.id,
						caseData: nextCase,
						impersonationContext,
					})
				: addCaseMutation.mutateAsync({
						templateId,
						stepId,
						caseData: nextCase,
						impersonationContext,
					}));
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			caseStatement,
			additionalTagFilters,
			primaryCriteria,
			templateId,
			stepId,
			impersonationContext,
			addCaseMutation,
			updateCaseMutation,
		]
	);

	const onPrimaryCriteriaChanged = React.useCallback(
		async (nextPrimaryCriteria?: Api.IContactFilterCriteria) => {
			if (!nextPrimaryCriteria) {
				// default case

				setPrimaryCriteria(null);

				saveCaseStatement(null);
				onStepSaved();
				return;
			}

			setPrimaryCriteria(nextPrimaryCriteria);

			if ((!!nextPrimaryCriteria.property && !!nextPrimaryCriteria.value) || !!nextPrimaryCriteria.criteria) {
				saveCaseStatement(nextPrimaryCriteria);
				onStepSaved();
			}
		},
		[saveCaseStatement, onStepSaved]
	);

	const onTagCriteriaCollectionChanged = React.useCallback(
		(criteria: Api.IContactFilterCriteria[]) => {
			setAdditionalTagFilters(criteria);
			saveCaseStatement(primaryCriteria, criteria);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[saveCaseStatement, additionalTagFilters, primaryCriteria]
	);
	// #endregion

	const primaryFilterStyles = React.useRef<StyleDeclarationValue[]>([styleSheet.primaryFilter]).current;
	const primaryFilterOperatorSelectBoxStyles = React.useRef<StyleDeclarationValue[]>([
		styleSheet.primaryFilterOperatorSelectBox,
	]).current;
	return (
		<div
			className={`${css(styleSheet.container, ...styles)} edit-automation-template-segment-header ${className || ''}`}
		>
			<div className={css(styleSheet.initialFilter)}>
				<div className={css(styleSheet.if)}>IF</div>
				<EditAutomationSegmentPrimaryFilter
					caseStatement={caseStatement}
					criteria={primaryCriteria}
					filterOperatorSelectBoxStyles={primaryFilterOperatorSelectBoxStyles}
					onCriteriaChanged={onPrimaryCriteriaChanged}
					styles={primaryFilterStyles}
					switchStep={switchStep}
					impersonationContext={impersonationContext}
				/>
			</div>
			{showAdditionalFilters ? (
				<div className={css(baseStyleSheet.verticalStack, styleSheet.tagFilters)}>
					<EditAutomationSegmentAdditionalFilters
						initialCriteria={additionalTagFilters}
						onCriteriaChanged={onTagCriteriaCollectionChanged}
						impersonationContext={impersonationContext}
					/>
				</div>
			) : (
				!!primaryCriteria && (
					<div className={css(styleSheet.footer)}>
						<button
							className={css(baseStyleSheet.horizontalStack, styleSheet.addFilterButton)}
							onClick={setShowAdditionalFiltersLambda(true)}
						>
							<RoundAddIcon fillColor={navigation} />
							<span>Add other filters</span>
						</button>
					</div>
				)
			)}
		</div>
	);
};
